/**
 * Thin typed API client. Every call carries the Cognito access token as a Bearer
 * token; the backend verifies it and enforces MFA.
 */

import type {
  CliCredentialsRequest,
  CliCredentialsResponse,
  ConsoleUrlRequest,
  ConsoleUrlResponse,
  CreateAccountRequest,
  CreateEntitlementRequest,
  DiscoverChildrenResponse,
  EnrollConfirmResponse,
  EnrollStartResponse,
  ListAccountsResponse,
  ListAuditResponse,
  ListEntitlementsResponse,
  MeResponse,
  MfaStatusResponse,
  TotpResetResponse,
  TotpVerifyResponse,
} from '@benilar/shared';

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: () => string | undefined,
  ) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const res = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });
    if (!res.ok) {
      let message = `${res.status} ${res.statusText}`;
      let code: string | undefined;
      try {
        const body = (await res.json()) as { message?: string; error?: string };
        if (body.message) message = body.message;
        if (body.error) code = body.error;
      } catch {
        /* ignore non-JSON error bodies */
      }
      const err = new Error(message) as Error & { code?: string };
      // Attach the server error code so callers can branch without string-matching.
      if (code) err.code = code;
      throw err;
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  me(): Promise<MeResponse> {
    return this.request<MeResponse>('/api/me');
  }

  consoleUrl(body: ConsoleUrlRequest): Promise<ConsoleUrlResponse> {
    return this.request<ConsoleUrlResponse>('/api/console-url', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  cliCredentials(body: CliCredentialsRequest): Promise<CliCredentialsResponse> {
    return this.request<CliCredentialsResponse>('/api/cli-credentials', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /* ----- TOTP step-up ----- */
  mfaStatus(): Promise<MfaStatusResponse> {
    return this.request<MfaStatusResponse>('/api/mfa/status');
  }

  mfaEnrollStart(): Promise<EnrollStartResponse> {
    return this.request<EnrollStartResponse>('/api/mfa/enroll/start', {
      method: 'POST',
    });
  }

  mfaEnrollConfirm(code: string): Promise<EnrollConfirmResponse> {
    return this.request<EnrollConfirmResponse>('/api/mfa/enroll/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  mfaVerify(code: string): Promise<TotpVerifyResponse> {
    return this.request<TotpVerifyResponse>('/api/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  mfaReset(code: string): Promise<TotpResetResponse> {
    return this.request<TotpResetResponse>('/api/mfa/reset', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  /* ----- admin ----- */
  listAccounts(): Promise<ListAccountsResponse> {
    return this.request<ListAccountsResponse>('/api/admin/accounts');
  }

  createAccount(body: CreateAccountRequest): Promise<void> {
    return this.request('/api/admin/accounts', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  discoverChildren(managementAccountId: string): Promise<DiscoverChildrenResponse> {
    return this.request<DiscoverChildrenResponse>('/api/admin/accounts/discover', {
      method: 'POST',
      body: JSON.stringify({ managementAccountId }),
    });
  }

  listEntitlements(): Promise<ListEntitlementsResponse> {
    return this.request<ListEntitlementsResponse>('/api/admin/entitlements');
  }

  createEntitlement(body: CreateEntitlementRequest): Promise<void> {
    return this.request('/api/admin/entitlements', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listAudit(principalSub?: string): Promise<ListAuditResponse> {
    const q = principalSub ? `?principalSub=${encodeURIComponent(principalSub)}` : '';
    return this.request<ListAuditResponse>(`/api/admin/audit${q}`);
  }
}
